import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './TwoPanelSelection.scss';

/**
 * TwoPanelSelection - High-performance dual-grid component for field selection
 * 
 * Features:
 * - Drag from left (available) to right (selected) to add items
 * - Drag from right to left to remove items
 * - Reorder items within right grid by dragging up/down
 * - Optimized with memoization and efficient state management
 * 
 * @param {Array} availableItems - All available database fields
 * @param {Array} initialSelectedItems - Pre-selected fields
 * @param {string} idField - Unique identifier field name (default: 'id')
 * @param {string} displayField - Field name to display (default: 'name')
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {string|number} height - Grid height (default: '500px')
 */
const TwoPanelSelection = ({ 
  availableItems = [],              // All available items to choose from
  initialSelectedItems = [],         // Items that are pre-selected
  idField = 'id',                   // Field name used as unique identifier for each item
  displayField = 'name',            // Field name to display in the grid
  onSelectionChange,                // Callback function triggered when selection changes
  height = '1000px'                 // Height of the grids
}) => {
  // State to track currently selected items
  // Initialize with initialSelectedItems and ensure it's always an array
  const [selectedItems, setSelectedItems] = useState(() => 
    Array.isArray(initialSelectedItems) ? initialSelectedItems : []
  );
  
  // Refs to store ag-grid API instances for both grids
  const leftGridApi = useRef(null);
  const rightGridApi = useRef(null);
  
  // Flags to track when each grid is fully initialized and ready
  const leftGridReady = useRef(false);
  const rightGridReady = useRef(false);

  // Sync selected items with parent component when initialSelectedItems changes
  useEffect(() => {
    // Ensure we always work with an array to prevent undefined/null issues
    const newSelectedItems = Array.isArray(initialSelectedItems) ? initialSelectedItems : [];
    setSelectedItems(newSelectedItems);
  }, [initialSelectedItems]);

  /**
   * Setup drop zones to enable cross-grid drag and drop functionality
   * This function configures each grid to accept drops from the other grid
   * It only runs when both grids are fully initialized
   */
  const setupDropZones = useCallback(() => {
    // Exit if either grid is not ready yet
    if (!leftGridApi.current || !rightGridApi.current || !leftGridReady.current || !rightGridReady.current) return;

    // Configure right grid to accept drops from left grid
    // When items are dropped from left to right, they get inserted at the drop position
    const rightDropZone = rightGridApi.current.getRowDropZoneParams({
      onDragStop: (params) => {
        // Support multi-row drag: use params.nodes if available, otherwise fall back to single node
        const draggedNodes = params.nodes && params.nodes.length > 0 ? params.nodes : [params.node];
        const draggedDataList = draggedNodes.map(node => node.data).filter(Boolean);
        
        if (draggedDataList.length === 0) return;
        
        setSelectedItems(prev => {
          // Filter out items that already exist in selected items to prevent duplicates
          const existingIds = new Set(prev.map(item => item[idField]));
          const newItems = draggedDataList.filter(item => !existingIds.has(item[idField]));
          if (newItems.length === 0) return prev;
          
          // Determine insertion index based on where items were dropped
          let insertIndex = prev.length; // Default to end if no overNode
          
          if (params.overNode) {
            // Get the index of the node we dropped over
            insertIndex = params.overNode.rowIndex !== undefined ? params.overNode.rowIndex : prev.length;
            
            // If there's a vertical direction indicator, adjust for dropping below
            // vDirection: 1 means dropping below, -1 means dropping above
            if (params.vDirection === 1) {
              insertIndex += 1;
            }
          }
          
          // Insert all dragged items at the specific position
          const newSelected = [...prev];
          newSelected.splice(insertIndex, 0, ...newItems);
          
          // Update column_order for all items based on their new positions
          const reorderedItems = newSelected.map((item, index) => ({
            ...item,
            column_order: index + 1
          }));
          
          // Notify parent component of the change
          if (onSelectionChange) {
            onSelectionChange(reorderedItems);
          }
          return reorderedItems;
        });
      }
    });

    // Configure left grid to accept drops from right grid
    // When items are dropped from right to left, they get removed from selected items
    const leftDropZone = leftGridApi.current.getRowDropZoneParams({
      onDragStop: (params) => {
        // Support multi-row drag: use params.nodes if available, otherwise fall back to single node
        const draggedNodes = params.nodes && params.nodes.length > 0 ? params.nodes : [params.node];
        const draggedDataList = draggedNodes.map(node => node.data).filter(Boolean);
        
        if (draggedDataList.length === 0) return;
        
        setSelectedItems(prev => {
          // Remove all dragged items from selected items and update column_order
          const idsToRemove = new Set(draggedDataList.map(item => item[idField]));
          const newSelected = prev
            .filter(item => !idsToRemove.has(item[idField]))
            .map((item, index) => ({
              ...item,
              column_order: index + 1
            }));
          
          // Notify parent component of the change
          if (onSelectionChange) {
            onSelectionChange(newSelected);
          }
          return newSelected;
        });
      }
    });

    // Register the drop zones with each grid
    // This tells ag-grid to accept drops from the opposite grid
    leftGridApi.current.addRowDropZone(rightDropZone);
    rightGridApi.current.addRowDropZone(leftDropZone);
  }, [idField, onSelectionChange]);

  /**
   * Compute available items (items not yet selected)
   * This automatically filters out any items that are in the selected list
   * Uses Set for O(1) lookup performance
   * Ensures initialSelectedItems are never shown in available items
   */
  const available = useMemo(() => {
    // Ensure availableItems is an array
    const items = Array.isArray(availableItems) ? availableItems : [];
    
    // Create a Set of selected IDs for fast lookup
    const selectedIds = new Set(selectedItems.map(item => item?.[idField]).filter(Boolean));
    
    // Filter out any items that are currently selected
    return items.filter(item => item && !selectedIds.has(item[idField]));
  }, [availableItems, selectedItems, idField]);

  /**
   * Checkbox selection in both grids is used ONLY to mark rows for multi-row drag.
   * Items are moved between panels exclusively via drag-and-drop, not by checking checkboxes.
   */

  /**
   * Base column definitions shared by both grids
   * Defines the data field to display and grid behavior
   */
  const columnDefs = useMemo(() => [
    {
      field: displayField,          // Field to display from data
      headerName: 'Name',           // Column header text
      flex: 1,                      // Take up available space
      sortable: true,               // Enable sorting
      filter: true,                 // Enable filtering
      suppressMenu: false, 
       menuTabs: ['filterMenuTab'],          // Show column menu
    }
  ], [displayField]);

  /**
   * Column definitions for left grid (Available Items)
   * Adds checkbox and drag handle columns
   */
  const leftColumnDefs = useMemo(() => [
    {
      checkboxSelection: true,     // Enable checkbox selection
      headerCheckboxSelection: true, // Enable header checkbox for select all
      maxWidth: 50,                // Small width for checkbox
      suppressMenu: true,          // No menu on checkbox column
      pinned: 'left',              // Pin to left side
    },
    {
      rowDrag: true,               // Enable row dragging
      maxWidth: 50,                // Small width for drag handle
      suppressMenu: true,          // No menu on drag column
    },
    ...columnDefs                  // Spread base column definitions
  ], [columnDefs]);

  /**
   * Column definitions for right grid (Selected Items)
   * Adds checkbox and drag handle columns
   */
  const rightColumnDefs = useMemo(() => [
    {
      checkboxSelection: true,     // Enable checkbox selection
      headerCheckboxSelection: true, // Enable header checkbox for select all
      maxWidth: 50,                // Small width for checkbox
      suppressMenu: true,          // No menu on checkbox column
      pinned: 'left',              // Pin to left side
    },
    {
      rowDrag: true,               // Enable row dragging
      maxWidth: 50,                // Small width for drag handle
      suppressMenu: true,          // No menu on drag column
    },
    ...columnDefs                  // Spread base column definitions
  ], [columnDefs]);

  /**
   * Handle reordering within the right grid (Selected Items)
   * This is triggered when user drags an item up or down within the right grid
   * ag-grid automatically handles the visual reordering, we just need to sync state
   */
  const onRightRowDragEnd = useCallback((event) => {
    // Only process if dropped over another row in the same grid
    if (event.overNode && rightGridApi.current) {
      // Collect all rows in their new order after ag-grid's reordering
      const allRows = [];
      rightGridApi.current.forEachNode(node => {
        if (node.data) allRows.push(node.data);
      });
      
      // Update column_order based on new position
      const reorderedItems = allRows.map((item, index) => ({
        ...item,
        column_order: index + 1
      }));
      
      // Update state with new order
      setSelectedItems(reorderedItems);
      
      // Notify parent component of the reordered items
      if (onSelectionChange) {
        onSelectionChange(reorderedItems);
      }
    }
  }, [onSelectionChange]);

  /**
   * Callback when left grid is initialized and ready
   * Stores the grid API and marks the grid as ready
   * Then attempts to setup drop zones if both grids are ready
   */
  const onLeftGridReady = useCallback((params) => {
    leftGridApi.current = params.api;
    leftGridReady.current = true;
    setupDropZones(); // Try to setup drop zones (will only work if both grids ready)
  }, [setupDropZones]);

  /**
   * Callback when right grid is initialized and ready
   * Stores the grid API and marks the grid as ready
   * Then attempts to setup drop zones if both grids are ready
   */
  const onRightGridReady = useCallback((params) => {
    rightGridApi.current = params.api;
    rightGridReady.current = true;
    setupDropZones(); // Try to setup drop zones (will only work if both grids ready)
  }, [setupDropZones]);

  return (
    <div className="two-panel-selection">
      <div className="panel-container">
        {/* 
          LEFT PANEL - Available Items
          Shows all items that haven't been selected yet
          Users can drag items from here to the right panel to select them
        */}
        <div className="panel left-panel">
          <div className="panel-header">
            <h3>Available Fields ({available.length})</h3>
            <p className="panel-instruction">Check items to select, then drag to right panel</p>
          </div>
          <div className="ag-theme-alpine" style={{ height, width: '100%' }}>
            <AgGridReact
              rowData={available}                              // Items to display (not selected)
              columnDefs={leftColumnDefs}                      // Column configuration with checkbox and drag handle
              rowSelection="multiple"                          // Enable multi-row selection
              suppressRowClickSelection={true}                 // Only select via checkbox
              rowDragManaged={false}                           // No internal reordering in left grid
              rowDragMultiRow={true}                           // Drag all selected rows together
              rowDragEntireRow={false}                         // Drag only from drag handle icon
              animateRows={true}                               // Smooth animations
              rowHeight={25}   
              headerHeight={30}                                // Row height in pixels
              getRowId={(params) => params.data[idField]}      // Unique ID for each row
              onGridReady={onLeftGridReady}                    // Called when grid is initialized
            />
          </div>
        </div>

        {/* 
          RIGHT PANEL - Selected Items
          Shows all selected items
          Users can:
          - Drag items back to left panel to deselect them
          - Drag items up/down within this panel to reorder them
        */}
        <div className="panel right-panel">
          <div className="panel-header">
            <h3>Selected Fields ({selectedItems.length})</h3>
            <p className="panel-instruction">Check items to select, then drag to left panel or reorder</p>
          </div>
          <div className="ag-theme-alpine" style={{ height, width: '100%' }}>
            <AgGridReact
              rowData={selectedItems}                          // Items to display (selected)
              columnDefs={rightColumnDefs}                     // Column configuration with checkbox and drag handle
              rowSelection="multiple"                          // Enable multi-row selection
              suppressRowClickSelection={true}                 // Only select via checkbox
              rowDragManaged={true}                            // Enable internal reordering
              rowDragMultiRow={true}                           // Drag all selected rows together
              rowDragEntireRow={false}                         // Drag only from drag handle icon
              animateRows={true}                               // Smooth animations
              rowHeight={25} 
                headerHeight={30}                                   // Row height in pixels
              getRowId={(params) => params.data[idField]}      // Unique ID for each row
              onGridReady={onRightGridReady}                   // Called when grid is initialized
              onRowDragEnd={onRightRowDragEnd}                 // Called after drag ends (for reordering)
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoPanelSelection;
